import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatCurrencyEUR, formatDateTimeIT } from "@/lib/formatters";
import { DateTimeField } from "@/components/TemporalField";
import { PageHeader } from "../components/PageHeader";
import { Link, useSearchParams } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const toIso = (value) => (value ? new Date(value).toISOString() : null);

export const RECOVERY_EVENT_STATUSES = [
  { key: "slot_released", label: "Slot liberato" },
  { key: "candidate_search", label: "Ricerca candidati" },
  { key: "no_candidate", label: "Nessun candidato" },
  { key: "proposals_prepared", label: "Proposte preparate" },
  { key: "proposal_accepted", label: "Proposta accettata" },
  { key: "recovered", label: "Recuperato" },
  { key: "expired", label: "Scaduto" },
  { key: "error", label: "Errore" },
];

const STATUS_LABELS = Object.fromEntries(
  RECOVERY_EVENT_STATUSES.map(({ key, label }) => [key, label])
);

const STATUS_TONES = {
  recovered: "border-emerald-200 bg-emerald-50 text-emerald-700",
  proposal_accepted: "border-emerald-200 bg-emerald-50 text-emerald-700",
  proposals_prepared: "border-sky-200 bg-sky-50 text-sky-700",
  candidate_search: "border-amber-200 bg-amber-50 text-amber-700",
  no_candidate: "border-slate-300 bg-slate-100 text-slate-700",
  expired: "border-slate-300 bg-slate-100 text-slate-700",
  error: "border-rose-200 bg-rose-50 text-rose-700",
  slot_released: "border-violet-200 bg-violet-50 text-violet-700",
};

const NOTIFICATION_LABELS = {
  sent: "Proposta pronta",
  confirmed: "Proposta accettata",
  expired: "Proposta scaduta",
  processing: "Conferma in corso",
  failed: "Proposta non disponibile",
};

const DEFAULT_FILTERS = {
  status: "",
  cancellation_id: "",
  customer_id: "",
  service_id: "",
  staff_id: "",
  after: "",
  before: "",
};

export const buildRecoveryEventParams = (filters, offset = 0) => {
  const params = { offset, limit: 20 };
  for (const key of ["status", "cancellation_id", "customer_id", "service_id", "staff_id"]) {
    if (filters[key]) {
      params[key] = filters[key];
    }
  }
  if (filters.after) {
    params.after = `${filters.after}T00:00:00.000Z`;
  }
  if (filters.before) {
    const nextDay = new Date(`${filters.before}T00:00:00.000Z`);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    params.before = nextDay.toISOString();
  }
  return params;
};

const hasQuery = (filters) => Object.values(filters).some(Boolean);
const customerRecoveryPath = (token) => `/customer/recovery/confirm/${encodeURIComponent(token)}`;

const customerRecoveryUrl = (token) => {
  const path = customerRecoveryPath(token);
  return typeof window === "undefined" ? path : new URL(path, window.location.origin).toString();
};

export function RecoveryPage() {
  const [searchParams] = useSearchParams();
  const linkedStatus = RECOVERY_EVENT_STATUSES.some(({ key }) => key === searchParams.get("status"))
    ? searchParams.get("status")
    : "";
  const linkedEventId = searchParams.get("event") || "";
  const initialFilters = { ...DEFAULT_FILTERS, status: linkedStatus, cancellation_id: linkedEventId };
  const initialFiltersRef = useRef(initialFilters);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState({});
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [notificationToConfirm, setNotificationToConfirm] = useState(null);
  const [confirmingNotification, setConfirmingNotification] = useState(false);
  const [requestForm, setRequestForm] = useState({
    customer_id: "",
    service_id: "",
    preferred_staff_id: "",
    requested_window_start: "",
    requested_window_end: "",
  });

  const loadReferenceData = async () => {
    setLoading(true);
    try {
      const [customersResponse, servicesResponse, staffResponse, summaryResponse] = await Promise.all([
        api.get("/customers"),
        api.get("/services"),
        api.get("/staff"),
        api.get("/recovery-events/summary"),
      ]);
      setCustomers(customersResponse.data);
      setServices(servicesResponse.data);
      setStaff(staffResponse.data);
      setSummary(summaryResponse.data.counts || {});
    } catch (error) {
      toast.error("Non riesco a caricare i dati di recupero. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async (nextFilters, offset = 0) => {
    if (!hasQuery(nextFilters)) {
      setEvents([]);
      setHasMore(false);
      return;
    }
    setEventsLoading(true);
    try {
      const response = await api.get("/recovery-events", {
        params: buildRecoveryEventParams(nextFilters, offset),
      });
      setEvents((current) => (offset === 0 ? response.data.items : [...current, ...response.data.items]));
      setHasMore(response.data.has_more);
    } catch (error) {
      toast.error("Non riesco a caricare gli eventi di recupero.");
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    loadReferenceData();
    if (hasQuery(initialFiltersRef.current)) {
      loadEvents(initialFiltersRef.current);
    }
  }, []);

  const selectStatus = (status) => {
    const nextFilters = { ...filters, status: filters.status === status ? "" : status };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    loadEvents(nextFilters);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(filters);
    loadEvents(filters);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setEvents([]);
    setHasMore(false);
  };

  const refreshAfterMutation = async () => {
    await loadReferenceData();
    if (hasQuery(appliedFilters)) {
      await loadEvents(appliedFilters);
    }
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    try {
      await api.post("/appointments", {
        customer_id: requestForm.customer_id,
        service_id: requestForm.service_id,
        preferred_staff_id: requestForm.preferred_staff_id || null,
        requested_window_start: toIso(requestForm.requested_window_start),
        requested_window_end: toIso(requestForm.requested_window_end),
        status: "requested",
        origin: "recovery",
      });
      toast.success("Cliente disponibile registrato.");
      setRequestForm({
        customer_id: "",
        service_id: "",
        preferred_staff_id: "",
        requested_window_start: "",
        requested_window_end: "",
      });
      await refreshAfterMutation();
    } catch (error) {
      toast.error("Impossibile creare la richiesta.");
    }
  };

  const confirmNotification = async (token) => {
    setConfirmingNotification(true);
    try {
      await api.post(`/notifications/confirm/${token}`);
      toast.success("Conferma registrata per conto del cliente.");
      setNotificationToConfirm(null);
      await refreshAfterMutation();
    } catch (error) {
      toast.error("Conferma non riuscita.");
    } finally {
      setConfirmingNotification(false);
    }
  };

  const openCustomerApp = (token) => {
    window.open(customerRecoveryPath(token), "_blank", "noopener,noreferrer");
  };

  const copyCustomerAppUrl = async (token) => {
    try {
      await navigator.clipboard.writeText(customerRecoveryUrl(token));
      toast.success("URL Customer App copiato.");
    } catch (error) {
      toast.error("Impossibile copiare l'URL Customer App.");
    }
  };

  if (loading) {
    return <div data-testid="recovery-loading">Caricamento Recupero Slot...</div>;
  }

  const queryActive = hasQuery(appliedFilters);

  return (
    <div className="space-y-6" data-testid="recovery-page">
      <PageHeader
        title="Recupero Slot"
        subtitle="Segui ogni slot liberato, dalla ricerca dei candidati fino all’eventuale recupero."
        titleTestId="recovery-title"
      />

      <section className="rounded-sm border border-slate-200 bg-white p-4 sm:p-6">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="recovery-summary">
          {RECOVERY_EVENT_STATUSES.map((status) => {
            const selected = filters.status === status.key;
            return (
              <button
                key={status.key}
                type="button"
                onClick={() => selectStatus(status.key)}
                aria-expanded={selected}
                className={`flex min-h-16 items-center justify-between rounded-sm border px-3 py-2 text-left text-sm ${
                  selected ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700"
                }`}
                data-testid={`recovery-status-${status.key}`}
              >
                <span className="font-medium">{status.label}</span>
                <span className={selected ? "text-slate-200" : "text-slate-500"}>
                  {summary[status.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        <form className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" onSubmit={applyFilters} data-testid="recovery-filters">
          <select name="customer_id" value={filters.customer_id} onChange={(event) => setFilters((current) => ({ ...current, customer_id: event.target.value }))} className="h-10 min-w-0 w-full max-w-full rounded-sm border border-slate-200 bg-white px-3 text-sm" aria-label="Cliente originario" data-testid="recovery-filter-customer">
            <option value="">Tutti i clienti</option>
            {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
          </select>
          <select name="service_id" value={filters.service_id} onChange={(event) => setFilters((current) => ({ ...current, service_id: event.target.value }))} className="h-10 min-w-0 w-full max-w-full rounded-sm border border-slate-200 bg-white px-3 text-sm" aria-label="Servizio" data-testid="recovery-filter-service">
            <option value="">Tutti i servizi</option>
            {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
          </select>
          <select name="staff_id" value={filters.staff_id} onChange={(event) => setFilters((current) => ({ ...current, staff_id: event.target.value }))} className="h-10 min-w-0 w-full max-w-full rounded-sm border border-slate-200 bg-white px-3 text-sm" aria-label="Staff" data-testid="recovery-filter-staff">
            <option value="">Tutto lo staff</option>
            {staff.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
          </select>
          <input type="date" name="after" value={filters.after} onChange={(event) => setFilters((current) => ({ ...current, after: event.target.value }))} className="h-10 min-w-0 w-full max-w-full rounded-sm border border-slate-200 px-3 text-sm" aria-label="Data iniziale" data-testid="recovery-filter-after" />
          <input type="date" name="before" value={filters.before} onChange={(event) => setFilters((current) => ({ ...current, before: event.target.value }))} className="h-10 min-w-0 w-full max-w-full rounded-sm border border-slate-200 px-3 text-sm" aria-label="Data finale" data-testid="recovery-filter-before" />
          <div className="flex gap-2">
            <button type="submit" className="h-10 rounded-sm bg-slate-900 px-4 text-sm font-medium text-white" data-testid="recovery-apply-filters">Applica</button>
            <button type="button" onClick={clearFilters} className="h-10 rounded-sm border border-slate-200 px-4 text-sm font-medium text-slate-700" data-testid="recovery-clear-filters">Pulisci</button>
          </div>
        </form>

        <div className="mt-5 space-y-3" data-testid="recovery-events">
          {!queryActive && (
            <p className="text-sm text-slate-500" data-testid="recovery-guidance">
              Scegli uno stato oppure applica i filtri per consultare gli eventi di recupero.
            </p>
          )}
          {eventsLoading && events.length === 0 && <p className="text-sm text-slate-500">Caricamento eventi...</p>}
          {queryActive && !eventsLoading && events.length === 0 && <p className="text-sm text-slate-500" data-testid="recovery-events-empty">Nessun evento trovato.</p>}
          {events.map((event) => (
            <details key={event.id} open={event.id === linkedEventId || undefined} className="rounded-sm border border-slate-200 p-4" data-testid={`recovery-event-${event.id}`}>
              <summary className="cursor-pointer list-none">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-900">{event.service?.name || "Servizio non disponibile"}</p>
                    <p className="mt-1 text-sm text-slate-500">
                      {event.start_time ? formatDateTimeIT(event.start_time) : formatDateTimeIT(event.cancelled_at)} · {event.customer?.name || "Cliente non disponibile"}
                    </p>
                  </div>
                  <span className={`rounded-sm border px-2 py-1 text-xs font-medium ${STATUS_TONES[event.status] || STATUS_TONES.slot_released}`}>
                    {STATUS_LABELS[event.status] || "Stato non disponibile"}
                  </span>
                </div>
              </summary>

              <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <p><span className="text-slate-500">Cliente originario:</span> {event.customer?.name || "Non disponibile"}</p>
                <p><span className="text-slate-500">Servizio:</span> {event.service?.name || "Non disponibile"}</p>
                <p><span className="text-slate-500">Staff:</span> {event.staff?.name || "Non disponibile"}</p>
                <p><span className="text-slate-500">Ricavo target:</span> {event.target_revenue == null ? "Non disponibile" : formatCurrencyEUR(event.target_revenue)}</p>
                <p><span className="text-slate-500">Candidati trovati:</span> {event.candidate_count}</p>
                <p><span className="text-slate-500">Proposte preparate:</span> {event.proposal_count}</p>
              </div>

              <ol className="mt-4 space-y-2 border-l-2 border-slate-200 pl-4 text-sm" data-testid={`recovery-timeline-${event.id}`}>
                <li><span className="font-medium">Slot liberato</span> · {formatDateTimeIT(event.cancelled_at)}</li>
                <li><span className="font-medium">Ricerca candidati</span> · {event.candidate_count} trovati</li>
                <li><span className="font-medium">Proposte preparate</span> · {event.proposal_count}</li>
                {event.accepted_count > 0 && <li><span className="font-medium">Proposta accettata</span> · {event.accepted_count}</li>}
                {event.recovered_appointment_id && <li className="text-emerald-700"><span className="font-medium">Appuntamento recuperato</span></li>}
              </ol>

              {event.recovered_appointment_id && event.start_time && (
                <Link
                  to={`/?date=${encodeURIComponent(event.start_time.slice(0, 10))}&appointment=${encodeURIComponent(event.recovered_appointment_id)}`}
                  className="mt-4 inline-flex rounded-sm bg-emerald-700 px-3 py-2 text-sm font-medium text-white"
                  data-testid={`recovery-open-appointment-${event.id}`}
                >
                  Apri appuntamento recuperato
                </Link>
              )}

              {event.notifications?.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium">Proposte</p>
                  {event.notifications.map((notification) => (
                    <div key={notification.id} className="rounded-sm border border-slate-200 p-3 text-sm" data-testid={`notification-${notification.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span>{notification.recipient || "Cliente non disponibile"}</span>
                        <span className="text-xs text-slate-500">{NOTIFICATION_LABELS[notification.status] || "Stato non disponibile"}</span>
                      </div>
                      {notification.status === "sent" && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" onClick={() => openCustomerApp(notification.token)} className="rounded-sm bg-slate-900 px-3 py-2 text-xs font-medium text-white" data-testid={`notification-open-customer-app-${notification.id}`}>Apri Customer App</button>
                          <button type="button" onClick={() => copyCustomerAppUrl(notification.token)} className="rounded-sm border border-slate-200 px-3 py-2 text-xs font-medium">Copia link</button>
                          <button type="button" onClick={() => setNotificationToConfirm(notification)} className="rounded-sm border border-amber-200 px-3 py-2 text-xs font-medium text-amber-900" data-testid={`notification-confirm-${notification.id}`}>Conferma diretta owner/Lab</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <details className="mt-4 text-xs text-slate-500">
                <summary className="cursor-pointer">Dettagli tecnici</summary>
                <div className="mt-2 space-y-1 break-all">
                  <p>ID cancellazione: {event.id}</p>
                  <p>ID appuntamento liberato: {event.slot_appointment_id || "Non disponibile"}</p>
                  <p>ID tentativo: {event.attempt?.id || "Non disponibile"}</p>
                  <p>ID appuntamento recuperato: {event.recovered_appointment_id || "Non disponibile"}</p>
                </div>
              </details>
            </details>
          ))}
          {hasMore && (
            <button type="button" onClick={() => loadEvents(appliedFilters, events.length)} disabled={eventsLoading} className="h-10 rounded-sm border border-slate-200 px-4 text-sm font-medium disabled:opacity-60" data-testid="recovery-load-more">
              {eventsLoading ? "Caricamento..." : "Carica altro"}
            </button>
          )}
        </div>
      </section>

      <details className="rounded-sm border border-slate-200 bg-white p-4 sm:p-6" data-testid="create-request-card">
        <summary className="cursor-pointer font-semibold" data-testid="create-request-title">Registra cliente disponibile per slot liberati</summary>
        <p className="mt-2 text-sm text-slate-500">Modalità supervisionata: registra una finestra flessibile senza inviare comunicazioni reali.</p>
        <form className="mt-4 space-y-3" onSubmit={submitRequest} data-testid="create-request-form">
          <Select value={requestForm.customer_id || undefined} onValueChange={(value) => setRequestForm((current) => ({ ...current, customer_id: value }))}>
            <SelectTrigger data-testid="request-customer-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
            <SelectContent>{customers.map((customer) => <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={requestForm.service_id || undefined} onValueChange={(value) => setRequestForm((current) => ({ ...current, service_id: value }))}>
            <SelectTrigger data-testid="request-service-select"><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
            <SelectContent>{services.map((service) => <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={requestForm.preferred_staff_id || "any"} onValueChange={(value) => setRequestForm((current) => ({ ...current, preferred_staff_id: value === "any" ? "" : value }))}>
            <SelectTrigger data-testid="request-staff-select"><SelectValue placeholder="Staff preferito" /></SelectTrigger>
            <SelectContent><SelectItem value="any">Qualsiasi staff</SelectItem>{staff.map((member) => <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="grid gap-3 md:grid-cols-2">
            <DateTimeField label="Inizio finestra richiesta" name="requested_window_start" value={requestForm.requested_window_start} onChange={(event) => setRequestForm((current) => ({ ...current, requested_window_start: event.target.value }))} data-testid="request-window-start" />
            <DateTimeField label="Fine finestra richiesta" name="requested_window_end" value={requestForm.requested_window_end} onChange={(event) => setRequestForm((current) => ({ ...current, requested_window_end: event.target.value }))} data-testid="request-window-end" />
          </div>
          <button type="submit" className="h-11 w-full rounded-sm border border-slate-200 bg-white font-medium" data-testid="request-submit-button">Registra richiesta di recupero</button>
        </form>
      </details>

      <AlertDialog open={Boolean(notificationToConfirm)} onOpenChange={(open) => !open && !confirmingNotification && setNotificationToConfirm(null)}>
        <AlertDialogContent data-testid="notification-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Usare il fallback owner/Lab?</AlertDialogTitle>
            <AlertDialogDescription>
              Usalo solo se il cliente ha già accettato fuori da FullBook. La conferma inserisce il candidato nello slot e chiude le altre proposte.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={confirmingNotification}>Annulla</AlertDialogCancel>
            <AlertDialogAction disabled={confirmingNotification} onClick={(event) => { event.preventDefault(); confirmNotification(notificationToConfirm?.token); }} data-testid="notification-confirm-accept">
              {confirmingNotification ? "Conferma..." : "Conferma diretta owner/Lab"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
